/**
 * Phase 27.39.2 — Team Google Calendar aggregate widget.
 *
 * KR admins get a live feed of every team member's upcoming Google
 * Calendar events in the next N days. Events are grouped by day and
 * annotated with the owner (avatar + name) so KR can see at a glance
 * who has what committed. Auto-refreshes every 60s while visible.
 *
 * Silently renders `null` when no team members have connected Google
 * yet — dashboards should degrade gracefully.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  CalendarDays, Loader2, RefreshCw, ExternalLink, Clock, MapPin,
  User as UserIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { UserAvatar } from '@/components/users/UserAvatar';
import { teamAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

function fmtDay(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch (_) {
    return '';
  }
}

function fmtTime(iso, allDay) {
  if (!iso) return '';
  if (allDay) return 'All day';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, {
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return '';
  }
}

function groupByDay(events) {
  const groups = new Map();
  events.forEach((ev) => {
    const key = (ev.start || '').split('T')[0] || 'no-date';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(ev);
  });
  return Array.from(groups.entries());
}

export function TeamCalendarWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState('14');
  const [error, setError] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    const now = new Date();
    const end = new Date(now.getTime() + parseInt(windowDays, 10) * 86400000);
    try {
      const r = await teamAPI.calendarEvents({
        start: now.toISOString(),
        end: end.toISOString(),
      });
      setData(r.data);
    } catch (err) {
      // 403 means the viewer isn't a KR admin — hide silently.
      if (err?.response?.status === 403) {
        setData({ events: [], members_with_calendar: 0 });
      } else {
        setError(getErrorMessage(err, 'Failed to load team calendar'));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [windowDays]);

  useEffect(() => { load(); }, [load]);

  // Poll every 60s while the tab is visible so KR sees new team events
  // (someone accepted an invite, someone got scheduled) without a manual refresh.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') load({ silent: true });
    };
    const iv = setInterval(tick, 60000);
    return () => clearInterval(iv);
  }, [load]);

  const grouped = useMemo(() => groupByDay(data?.events || []), [data]);

  if (loading && !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <CalendarDays size={16} className="text-sky-600" />
            Team Calendar
          </CardTitle>
        </CardHeader>
        <CardContent className="py-8 flex items-center justify-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading events…
        </CardContent>
      </Card>
    );
  }

  if (!data || (data.members_with_calendar === 0 && (data.events || []).length === 0)) {
    return (
      <Card data-testid="team-calendar-widget-empty">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <CalendarDays size={16} className="text-sky-600" />
            Team Calendar
          </CardTitle>
        </CardHeader>
        <CardContent className="py-6 text-center text-sm text-slate-500">
          No team members have connected Google Calendar yet.
          <br />
          <span className="text-xs text-slate-400">
            When they connect their calendar from their profile, their events
            will show up here automatically.
          </span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="team-calendar-widget">
      <CardHeader className="flex flex-row items-start justify-between pb-3">
        <div>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <CalendarDays size={16} className="text-sky-600" />
            Team Calendar
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Google Calendar events from {data.members_with_calendar}{' '}
            connected team member{data.members_with_calendar === 1 ? '' : 's'}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={windowDays} onValueChange={setWindowDays}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid="team-calendar-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="14">14 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost" size="sm"
            onClick={() => load({ silent: false })}
            disabled={loading}
            data-testid="team-calendar-refresh"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 p-2 rounded">
            {error}
          </div>
        )}
        {grouped.length === 0 ? (
          <div className="text-center py-6 text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg bg-slate-50">
            No scheduled events in this window.
          </div>
        ) : (
          <div className="space-y-4">
            {grouped.map(([day, events]) => (
              <div key={day}>
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                  <Clock size={11} />
                  {fmtDay(day + 'T00:00:00Z')} · {events.length} event{events.length === 1 ? '' : 's'}
                </div>
                <div className="space-y-2">
                  {events.map((ev, i) => (
                    <div
                      key={`${ev.id || i}-${ev.owner?.user_id || 'x'}`}
                      className="flex gap-3 rounded-lg border border-slate-200 bg-white p-3 hover:border-sky-200 hover:shadow-sm transition-all"
                      data-testid={`team-calendar-event-${i}`}
                    >
                      <UserAvatar
                        user={ev.owner}
                        size="w-8 h-8"
                        textSize="text-[9px]"
                        testId={`team-calendar-owner-${i}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="text-sm font-semibold text-slate-900 truncate">
                            {ev.summary}
                          </h4>
                          {ev.html_link && (
                            <a
                              href={ev.html_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-slate-400 hover:text-sky-600 shrink-0"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500">
                          <span className="inline-flex items-center gap-1">
                            <Clock size={11} />
                            {fmtTime(ev.start, ev.all_day)}
                          </span>
                          {ev.location && (
                            <span className="inline-flex items-center gap-1 truncate">
                              <MapPin size={11} />
                              {ev.location}
                            </span>
                          )}
                          <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200">
                            <UserIcon size={9} className="mr-1" />
                            {ev.owner?.name || 'unknown'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default TeamCalendarWidget;
